// CHG-1290 / ITER-3415 — central permission-gate catalog.
//
// Declarative source of truth mapping each v2 entity's affordance IDs
// (action button id strings) to the required permission code. Consumers
// (per-page wiring CHGs that follow this one) read this catalog +
// gateActions() to filter PageHeaderAction[] before passing to the
// composition primitives.
//
// Why a separate catalog instead of inline `permission_gate` fields on
// each PageHeaderAction in the generator slot configs:
//   - PageHeaderAction is a shipped composition-primitive type (CHG-1231);
//     adding a field is a primitive mutation (forbidden by CHG-1289's
//     immutability constraint).
//   - The catalog lives in apps/dashboard-v2/src/auth/ (a CHG-1289 module)
//     so future primitive-extension CHGs can wire `gateActions(actions,
//     catalog.donor_list, hasPermission)` page-side without touching the
//     primitive OR the generator slot.
//
// Backend authority: this catalog is a FE UX optimization. The BE
// require_permission dependency on each route is authoritative; if the
// FE has stale permissions and the user clicks an affordance whose
// endpoint they can't reach, the BE returns 403 (handled by the
// CHG-1289 v2-api-forbidden CustomEvent interceptor).

export interface GateRule {
  /** Required permission code (e.g. "alora-giving.donor.create"). */
  permission: string;
  /** What to do when the user lacks the permission. Default: "hide". */
  fallback?: "hide" | "disable";
}

/** Map from action-id → gate rule. */
export type ActionGateMap = Record<string, GateRule>;

// ─────────────────────────────────────────────────────────────────
// Permission codes — pulled from src/api/app/core/deps.py
// _PERMISSION_ROLE_MAP (CHG-1289 mirror). Keep in sync with the BE table.
// ─────────────────────────────────────────────────────────────────

const P = {
  donor: {
    create: "alora-giving.donor.create",
    update: "alora-giving.donor.update",
    delete: "alora-giving.donor.delete",
    export: "alora-giving.donor.export",
  },
  donation: {
    create: "alora-giving.donation.create",
    update: "alora-giving.donation.update",
    refund: "alora-giving.donation.refund",
    // CHG-1301 — donation audit-log read authority (BE seeded in
    // specs/seeds/donation-engine/permissions.yaml + registered in
    // src/api/app/core/deps.py _PERMISSION_ROLE_MAP). Closes CHG-1294
    // deferred follow-up #2 (Audit tab gating).
    audit_read: "alora-giving.donation.audit_read",
  },
  pledge: {
    create: "alora-giving.pledge.create",
    update: "alora-giving.pledge.update",
  },
  campaign: {
    create: "alora-giving.campaign.create",
    update: "alora-giving.campaign.update",
  },
  recurring: {
    create: "alora-giving.recurring-donation.create",
    update: "alora-giving.recurring-donation.update",
    // RDM-R08 — the BE enforces DISTINCT permissions per lifecycle verb
    // (recurring_donations router): pause→.pause, resume→.resume,
    // cancel→.cancel, reconcile→.update. Previously resume/cancel mapped to
    // .update which over-granted on the FE (BE still 403s, but the gate UX
    // was wrong).
    pause: "alora-giving.recurring-donation.pause",
    resume: "alora-giving.recurring-donation.resume",
    cancel: "alora-giving.recurring-donation.cancel",
  },
  designation: {
    create: "alora-giving.designation.create",
    update: "alora-giving.designation.update",
  },
  asset: {
    create: "alora-giving.asset.create",
    update: "alora-giving.asset.update",
    delete: "alora-giving.asset.delete",
  },
  event: {
    create: "alora-giving.event.create",
    update: "alora-giving.event.update",
  },
  message: {
    create: "alora-giving.message.create",
    resend: "alora-giving.message.resend",
  },
  template: {
    create: "alora-giving.notification-template.create",
    update: "alora-giving.notification-template.update",
    delete: "alora-giving.notification-template.delete",
  },
  tag: {
    create: "alora-giving.tag.create",
    update: "alora-giving.tag.update",
    delete: "alora-giving.tag.delete",
    // CHG-1305 / ITER-3432 — Tag taxonomy phase 1 (T2 + alembic +
    // permissions seed). Codes now backed by:
    //   specs/seeds/tag-management/permissions.yaml
    //   src/api/app/core/deps.py _PERMISSION_ROLE_MAP
    // Additive only — read/attach/detach are NEW keys; existing
    // create/update/delete keys unchanged (CHG-1290 doctrine).
    read: "alora-giving.tag.read",
    attach: "alora-giving.tag.attach",
    detach: "alora-giving.tag.detach",
  },
  team: {
    invite: "alora-giving.user.invite",
    update: "alora-giving.user.update",
    delete: "alora-giving.user.delete",
    // CHG-1301 — role-assignment authority (BE seeded in
    // specs/seeds/user-management/permissions.yaml + registered in
    // src/api/app/core/deps.py _PERMISSION_ROLE_MAP). Closes CHG-1294
    // deferred follow-up #2 (Permissions tab gating). CHG-2951 (Slice F /
    // §8 D2): the BE now enforces this code on all role-assignment
    // mutation routes, closing the UM-R6 UI/BE gate mismatch.
    role_update: "alora-giving.user.role_update",
    // CHG-2951 (Slice F / §8 D3) — admin MFA reset authority. BE enforces
    // alora-giving.user-mfa.reset on POST /users/{id}/mfa/admin-reset +
    // legacy DELETE /users/{id}/mfa (was the coarse user.update).
    mfa_reset: "alora-giving.user-mfa.reset",
    // CHG-2958 (Slice J / UM-R12) — admin password-reset authority. BE
    // enforces alora-giving.user.resetpassword on
    // POST /users/{id}/password-reset (users.py request_password_reset);
    // the UI gate now cites the code-exact permission (was the
    // holder-set-equivalent P.team.update heuristic).
    resetpassword: "alora-giving.user.resetpassword",
    // CHG-2958 (Slice J / UM-R12) — Sessions tab data authority. BE
    // enforces alora-giving.platform.member.read on
    // GET /platform/members/accounts/{id}/sessions
    // (routers/platform_members.py list_account_sessions, CHG-2957 /
    // Slice H). Deliberately NOT user.read — sessions expose IP/UA
    // telemetry beyond the org-staff user-read surface.
    platform_member_read: "alora-giving.platform.member.read",
  },
  import_batch: {
    create: "alora-giving.import.create",
    cancel: "alora-giving.import.cancel",
  },
  settings: {
    update: "alora-giving.settings.update",
  },
  // CHG-2460 — Widget Studio (S3). Codes backed by
  // src/api/app/core/deps.py _PERMISSION_ROLE_MAP (widget-platform.*) and the
  // widget_platform_admin router constants (_READ / _CONFIGURE / _APPROVE).
  widget: {
    read: "alora-giving.widget-platform.read",
    configure: "alora-giving.widget-platform.configure",
    approve: "alora-giving.widget-platform.approve",
  },
} as const;

// ─────────────────────────────────────────────────────────────────
// Per-entity gate maps. Action IDs match the generator slot
// PageHeaderAction.id values; the gateActions helper joins them.
// ─────────────────────────────────────────────────────────────────

export const GATE_CATALOG = {
  donor_list: {
    "record-donation": { permission: P.donation.create },
    "record-pledge":   { permission: P.pledge.create },
    "export-history":  { permission: P.donor.export },
  } satisfies ActionGateMap,

  donor_detail: {
    "edit":            { permission: P.donor.update },
    "record-donation": { permission: P.donation.create },
    "record-pledge":   { permission: P.pledge.create },
  } satisfies ActionGateMap,

  asset_list: {
    "upload":      { permission: P.asset.create },
    "generate-ai": { permission: P.asset.create },
  } satisfies ActionGateMap,

  asset_detail: {
    "edit":            { permission: P.asset.update },
    "replace-version": { permission: P.asset.update },
    // "download" is intentionally not gated — viewers can download.
  } satisfies ActionGateMap,

  campaign_list: {
    "edit":      { permission: P.campaign.create },  // "New campaign" reuses id="edit"
    "duplicate": { permission: P.campaign.create },
  } satisfies ActionGateMap,

  campaign_detail: {
    "edit": { permission: P.campaign.update },
    // CHG-1294 — additive entries for actual campaign-detail.ts action ids.
    "activate": { permission: P.campaign.update },
    "archive":  { permission: P.campaign.update },
  } satisfies ActionGateMap,

  recurring_list: {
    // CHG-1290 original
    "create": { permission: P.recurring.create },
    // CHG-1294 — gate codes for the action IDs actually present in
    // apps/dashboard-v2/src/generated/screens/recurring-list.ts header
    // actions. Purely additive to CHG-1290 — no shipped keys mutated.
    "edit":  { permission: P.recurring.update },
    "pause": { permission: P.recurring.pause },
  } satisfies ActionGateMap,

  recurring_detail: {
    "edit":  { permission: P.recurring.update },
    "pause": { permission: P.recurring.pause },
    // RDM-R08 — corrected to the BE's distinct per-verb permissions
    // (were both .update, over-granting on the FE). reconcile is the
    // needs_reconciliation un-park action (recurring-donation.update).
    "resume": { permission: P.recurring.resume },
    "cancel": { permission: P.recurring.cancel },
    "reconcile": { permission: P.recurring.update },
  } satisfies ActionGateMap,

  donation_list: {
    "record-donation": { permission: P.donation.create },
    "export":          { permission: P.donor.export },
  } satisfies ActionGateMap,

  donation_detail: {
    "edit":   { permission: P.donation.update },
    "refund": { permission: P.donation.refund },
    // CHG-1294 — additive entries for actual donation-detail.ts action ids.
    "view-receipt":   { permission: P.donor.export },        // viewer-level read affordance
    "send-receipt":   { permission: P.donation.update },
    "mark-refunded":  { permission: P.donation.refund },
    // CHG-1301 — Audit tab gate. Consumed via DetailPageTab.permission
    // (CHG-1292) in apps/dashboard-v2/src/generated/screens/donation-detail.ts.
    // Closes CHG-1294 deferred follow-up #2.
    "audit_tab": { permission: P.donation.audit_read },
  } satisfies ActionGateMap,

  pledge_list: {
    // CHG-1290 original
    "record-pledge": { permission: P.pledge.create },
    // CHG-1294 — additive entries for actual pledge-list.ts action ids.
    "record-payment": { permission: P.pledge.update },
    "send-reminder":  { permission: P.message.resend },
  } satisfies ActionGateMap,

  pledge_detail: {
    "edit": { permission: P.pledge.update },
    // CHG-1294 — additive entries for actual pledge-detail.ts action ids.
    "record-payment": { permission: P.pledge.update },
    "send-reminder":  { permission: P.message.resend },
  } satisfies ActionGateMap,

  designation_list: {
    // CHG-1290 original
    "create": { permission: P.designation.create },
    // CHG-1294 — additive entries for actual designation-list.ts action ids.
    "edit":    { permission: P.designation.update },
    "reorder": { permission: P.designation.update },
  } satisfies ActionGateMap,

  designation_detail: {
    "edit": { permission: P.designation.update },
    // CHG-1294 — additive entries for actual designation-detail.ts action ids.
    "complete": { permission: P.designation.update },
    "archive":  { permission: P.designation.update },
  } satisfies ActionGateMap,

  template_list: {
    // CHG-1290 original
    "create": { permission: P.template.create },
    // CHG-1294 — additive entries for actual template-list.ts action ids.
    "edit":      { permission: P.template.update },
    "duplicate": { permission: P.template.create },
  } satisfies ActionGateMap,

  template_detail: {
    // CHG-1285 TemplateEditor's Edit button — closed by CHG-1288 backend.
    // CHG-1294 wires the gate through PageHeader; replaces the
    // CHG-1285 hardcoded canEdit prop check (transition documented in
    // template-detail.ts header).
    "edit":   { permission: P.template.update },
    "delete": { permission: P.template.delete },
    // CHG-1294 — additive entries for actual template-detail.ts action ids.
    "publish":   { permission: P.template.update },
    "test-send": { permission: P.message.resend },
  } satisfies ActionGateMap,

  tag_list: {
    // CHG-1290 original
    "create": { permission: P.tag.create },
    // CHG-1294 — additive entries for actual tag-list.ts action ids.
    "edit":  { permission: P.tag.update },
    "merge": { permission: P.tag.update },
    // CHG-1305 — bulk-action gates. Forward-promised — the tag-list
    // generator slot config does not surface bulk actions in phase 1
    // (no FE picker / bulk-tag toolbar until phase 3). Registered here
    // so phase-3 wiring picks them up without a CHG-1290 mutation.
    // Additive only.
    "bulk-delete":   { permission: P.tag.delete },
    "bulk-merge":    { permission: P.tag.update },
    "bulk-recolor":  { permission: P.tag.update },
  } satisfies ActionGateMap,

  tag_detail: {
    "edit":   { permission: P.tag.update },
    "delete": { permission: P.tag.delete },
    // CHG-1294 — additive entries for actual tag-detail.ts action ids.
    "merge": { permission: P.tag.update },
    // CHG-1305 — detach-from-entity (the inverse of attach) is reached
    // through per-row affordances on the four detail tabs (donors /
    // campaigns / recurring / pledges). Forward-promised for phase 3
    // when the per-application detach button lands. Additive only.
    "detach": { permission: P.tag.detach },
  } satisfies ActionGateMap,

  team_list: {
    "invite": { permission: P.team.invite },
    // CHG-3357 / QA #309 — DELETE /users/{id} enforces user.update.
    "remove": { permission: P.team.update },
  } satisfies ActionGateMap,

  team_detail: {
    "edit":   { permission: P.team.update },
    // CHG-3357 / QA #309 — code-exact with removeOrgMember.
    "remove": { permission: P.team.update },
    // CHG-1294 — additive entries for actual team-member-detail.ts action ids.
    // CHG-2958 (Slice J) — code-exact gate: BE enforces user.resetpassword
    // on POST /users/{id}/password-reset (was P.team.update heuristic).
    "reset-password": { permission: P.team.resetpassword },
    // CHG-2951 (Slice F / §8 D3) — reset-mfa now gates on the dedicated
    // user-mfa.reset code the BE enforces (was P.team.update), matching
    // the team-member entity-hint's resetAccountMfa permission.
    "reset-mfa":      { permission: P.team.mfa_reset },
    // CHG-1301 — Permissions tab gate. Consumed via
    // DetailPageTab.permission (CHG-1292) in
    // apps/dashboard-v2/src/generated/screens/team-member-detail.ts.
    // Closes CHG-1294 deferred follow-up #2.
    "permissions_tab": { permission: P.team.role_update },
    // CHG-2958 (Slice J / UM-R12) — Sessions tab gate. Consumed via
    // DetailPageTab.permission, hides the tab for holders lacking the
    // platform-members read authority the BE enforces on the wired
    // GET /platform/members/accounts/{id}/sessions data source.
    "sessions_tab": { permission: P.team.platform_member_read },
  } satisfies ActionGateMap,

  event_list: {
    // CHG-1290 original
    "create": { permission: P.event.create },
    // CHG-1294 — additive entries for actual event-list.ts action ids.
    "edit":      { permission: P.event.update },
    "duplicate": { permission: P.event.create },
  } satisfies ActionGateMap,

  event_detail: {
    "edit": { permission: P.event.update },
    // CHG-1294 — additive entries for actual event-detail.ts action ids.
    "publish": { permission: P.event.update },
    "archive": { permission: P.event.update },
  } satisfies ActionGateMap,

  message_list: {
    // CHG-1290 original
    "broadcast": { permission: P.message.create },
    "resend":    { permission: P.message.resend },
    // CHG-1294 — additive entries for actual message-list.ts action ids.
    "edit":      { permission: P.message.create },
    "duplicate": { permission: P.message.create },
  } satisfies ActionGateMap,

  message_detail: {
    "resend": { permission: P.message.resend },
    // CHG-1294 — additive entries for actual message-detail.ts action ids.
    "edit":    { permission: P.message.create },
    "publish": { permission: P.message.create },
    "cancel":  { permission: P.message.create },
  } satisfies ActionGateMap,

  import_batch_list: {
    "import": { permission: P.import_batch.create },
  } satisfies ActionGateMap,

  import_batch_detail: {
    "cancel": { permission: P.import_batch.cancel },
  } satisfies ActionGateMap,

  settings: {
    // CHG-1239 SettingsForm save button — gated on per-section permission;
    // map at the section level (brand_identity → settings.update, etc.).
    "save": { permission: P.settings.update },
  } satisfies ActionGateMap,

  // CHG-2460 — Widget Studio (S3). Action ids match the generated
  // widget-instance-detail.ts header-action ids. Lifecycle verbs
  // (approve/activate/disable/block) gate on widget-platform.approve; the
  // presentation-only Edit gates on widget-platform.configure. Each is ALSO
  // when:-gated on the current activation_status in the detail actions
  // override-block so only the legal transition renders.
  widget_detail: {
    "edit":     { permission: P.widget.configure },
    "approve":  { permission: P.widget.approve },
    "activate": { permission: P.widget.approve },
    "disable":  { permission: P.widget.approve },
    "block":    { permission: P.widget.approve },
  } satisfies ActionGateMap,

  // CHG-2463 — Widget Studio S4 (installations). Inline status override is the
  // only write (configure); view-instance is a read-level navigation affordance.
  widget_installation_detail: {
    "override-status": { permission: P.widget.configure },
    "view-instance":   { permission: P.widget.read },
  } satisfies ActionGateMap,
} as const;

export type CatalogKey = keyof typeof GATE_CATALOG;
